import type { CSSProperties, ComponentPropsWithRef, ReactNode } from "react";
import { forwardRef } from "react";
import { omit } from "lodash";
import { isEmpty } from "lodash";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { getBlocksFromChildren } from "@circle-react-shared/BuilderUI/Helpers/blockUtils";
import { useAttachmentMetadata } from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseSizing } from "@circle-react-shared/BuilderUI/utils/parseSizing";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { defaultStyles } from "./defaultStyles";

interface StylesBackgroundImageValue {
  placeholder: string;
  signed_id: string;
}

interface SectionProps extends Omit<ComponentPropsWithRef<"div">, "style"> {
  ariaLabel?: string;
  children: ReactNode;
  role?: string;
  styles?: Readonly<Record<string, StyleValue>>;
}

export const Section = forwardRef<HTMLDivElement, SectionProps>(
  ({ children, styles = {}, ariaLabel, role, className, ...rest }, ref) => {
    const { backgroundImage } = styles;
    const { value } = backgroundImage || {};
    const { signed_id: backgroundImageSignedId, placeholder } =
      (value as StylesBackgroundImageValue) || {
        signed_id: "",
        placeholder: "",
      };

    const attachmentMetadata = useAttachmentMetadata(backgroundImageSignedId);
    const { url: attachmentUrl } = attachmentMetadata ?? {};
    const url = attachmentUrl || placeholder;

    const inlineStyles: CSSProperties = {
      ...parseStyles({
        styles: {
          backgroundImage: {
            type: "background-image",
            value: url,
          },
          ...omit(styles, ["backgroundImage", "sizing"]),
        },
        defaultStyles,
      }),
      ...parseSizing({
        sizing: styles.sizing,
        defaultSizing: defaultStyles.sizing,
      }),
    };

    const childrenBlocks = getBlocksFromChildren(children);

    return (
      <div
        ref={ref}
        className={classNames(
          "wb-p-5 wb-w-full wb-h-auto wb-min-h-24 wb-bg-transparent",
          {
            "wb-border-gray-500 wb-border wb-border-dashed wb-shadow-[inset_0_0_2px_4px_rgba(0,0,0,0.1),inset_0_0_2px_4px_rgba(255,255,255,0.2),inset_0_0_3px_rgba(255,255,255,0.3)]":
              isEmpty(childrenBlocks),
          },
          className,
        )}
        style={inlineStyles}
        aria-label={ariaLabel}
        role={role}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

Section.displayName = "Section";
